import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Combobox, Dialog, Popover, Transition } from "@headlessui/react";
import {
  CheckIcon,
  PencilIcon,
  PlusIcon,
  XIcon,
} from "@heroicons/react/outline";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const TAG_STYLES = {
  red: "bg-red-50 text-red-700 ring-red-200",
  orange: "bg-orange-50 text-orange-700 ring-orange-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  pink: "bg-pink-50 text-pink-700 ring-pink-200",
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  lime: "bg-lime-50 text-lime-700 ring-lime-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
};

const JOB_STATUS_OPTIONS = [
  { id: "U", name: "Submitted" },
  { id: "A", name: "Received" },
  { id: "S", name: "Assigned" },
  { id: "W", name: "In Progress" },
  { id: "R", name: "Review" },
  { id: "T", name: "Canceled" },
  { id: "C", name: "Complete" },
];

export function CardEditButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
    >
      <PencilIcon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

function EditDrawer({ open, onClose, title, description, saving, onSave, children }) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[11000]"
        onClose={saving ? () => {} : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/10 backdrop-blur-[1px]" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-8 sm:pl-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-200"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto flex h-full w-[92vw] max-w-[480px] flex-col border-l border-slate-200 bg-white text-sm shadow-xl">
                <div className="relative border-b border-slate-200 px-5 py-5">
                  <span className="inline-flex h-5 items-center rounded-full border border-sky-200 bg-sky-50 px-2 text-xs font-medium text-sky-700">
                    Edit job
                  </span>
                  <Dialog.Title className="mt-2 text-lg font-extrabold text-slate-950">
                    {title}
                  </Dialog.Title>
                  <Dialog.Description className="mt-0.5 text-sm text-slate-500">
                    {description}
                  </Dialog.Description>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
                  >
                    <XIcon className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">Close</span>
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
                <div className="mt-auto grid grid-cols-2 gap-2 border-t border-slate-200 p-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onSave}
                    disabled={saving}
                    className="inline-flex h-9 items-center justify-center rounded-lg bg-[#E3222A] px-3 font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function Field({ label, name, value, onChange, type = "text", min, max, step, autoCapitalize }) {
  return (
    <label className="block" htmlFor={`inline-edit-${name}`}>
      <span className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
        {label}
      </span>
      <input
        id={`inline-edit-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
        autoCapitalize={autoCapitalize}
        className="mt-2 block h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 shadow-sm focus:border-slate-500 focus:ring-slate-200"
      />
    </label>
  );
}

function MilitaryDateTimeField({ label, name, value, onChange }) {
  const [date = "", time = ""] = String(value || "").split("T");
  const [hour = "00", minute = "00"] = time.split(":");
  const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));

  const updateValue = (nextDate, nextTime) => {
    onChange({
      target: {
        name,
        value: nextDate ? `${nextDate}T${nextTime || "00:00"}` : "",
      },
    });
  };

  return (
    <fieldset>
      <legend className="w-full text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
        <span className="flex items-center justify-between gap-2">
          <span>{label} <span className="font-medium normal-case tracking-normal">(24-hour time)</span></span>
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()} date`}
            disabled={!value}
            onClick={() => updateValue("", "")}
            className="inline-flex h-8 flex-none items-center rounded-md border border-slate-200 bg-white px-2 text-xs font-medium normal-case tracking-normal text-slate-600 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
          >
            Clear
          </button>
        </span>
      </legend>
      <div className="mt-2 grid grid-cols-[minmax(0,1fr)_8.5rem] gap-2">
        <label>
          <span className="sr-only">{label} date</span>
          <input
            aria-label={`${label} date`}
            type="date"
            value={date}
            onChange={(event) => updateValue(event.target.value, time)}
            className="block h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 shadow-sm focus:border-slate-500 focus:ring-slate-200"
          />
        </label>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1" role="group" aria-label={`${label} time (24-hour)`}>
          <label>
            <span className="sr-only">{label} hour (24-hour)</span>
            <select
              aria-label={`${label} hour (24-hour)`}
              value={hour}
              disabled={!date}
              onChange={(event) => updateValue(date, `${event.target.value}:${minute}`)}
              className="block h-10 w-full rounded-lg border border-slate-300 px-1 text-sm text-slate-800 shadow-sm focus:border-slate-500 focus:ring-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
            >
              {hours.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <span className="font-bold text-slate-500" aria-hidden="true">:</span>
          <label>
            <span className="sr-only">{label} minute</span>
            <select
              aria-label={`${label} minute`}
              value={minute}
              disabled={!date}
              onChange={(event) => updateValue(date, `${hour}:${event.target.value}`)}
              className="block h-10 w-full rounded-lg border border-slate-300 px-1 text-sm text-slate-800 shadow-sm focus:border-slate-500 focus:ring-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
            >
              {minutes.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
        </div>
      </div>
    </fieldset>
  );
}

function SelectField({ label, name, value, onChange, options, emptyLabel }) {
  return (
    <label className="block" htmlFor={`inline-edit-${name}`}>
      <span className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
        {label}
      </span>
      <select
        id={`inline-edit-${name}`}
        name={name}
        value={value}
        onChange={onChange}
        className="mt-2 block h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 shadow-sm focus:border-slate-500 focus:ring-slate-200"
      >
        {emptyLabel && <option value="">{emptyLabel}</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function uniqueOptions(options) {
  return options.filter(
    (option, index, list) =>
      option &&
      list.findIndex((candidate) => String(candidate?.id) === String(option.id)) === index,
  );
}

function SearchSelectField({
  label,
  name,
  value,
  onSelect,
  onSearch,
  initialOptions = [],
  enabled,
  emptyLabel,
  optionLabel = (option) => option?.name || "",
}) {
  const [query, setQuery] = useState("");
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [chosenOption, setChosenOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(onSearch);
  const options = uniqueOptions([...initialOptions, ...remoteOptions, chosenOption]);
  const selected = options.find((option) => String(option.id) === String(value)) || null;

  useEffect(() => {
    searchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchRef.current(query.trim());
        if (active) setRemoteOptions(results || []);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [enabled, query]);

  return (
    <Combobox
      as="div"
      value={selected}
      onChange={(option) => {
        setChosenOption(option || null);
        onSelect(option || null);
      }}
      nullable
    >
      <Combobox.Label className="block text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
        {label}
      </Combobox.Label>
      <div className="relative mt-2">
        <Combobox.Input
          name={name}
          displayValue={(option) => optionLabel(option)}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${label.toLowerCase()}`}
          autoComplete="off"
          className="block h-10 w-full rounded-lg border border-slate-300 px-3 pr-10 text-sm text-slate-800 shadow-sm focus:border-slate-500 focus:ring-slate-200"
        />
        <Combobox.Button aria-label={`Open ${label} options`} className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </Combobox.Button>
        <Transition as={Fragment} leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0" afterLeave={() => setQuery("")}>
          <Combobox.Options className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-xl focus:outline-none">
            {emptyLabel && (
              <Combobox.Option value={null} className={({ active }) => classNames("cursor-pointer px-3 py-2", active ? "bg-slate-100 text-slate-950" : "text-slate-600")}>
                {emptyLabel}
              </Combobox.Option>
            )}
            {loading && <div className="px-3 py-2 text-slate-500">Searching…</div>}
            {!loading && !options.length && <div className="px-3 py-2 text-slate-500">No results found.</div>}
            {!loading && options.map((option) => (
              <Combobox.Option
                key={option.id}
                value={option}
                className={({ active }) => classNames("cursor-pointer px-3 py-2", active ? "bg-slate-100 text-slate-950" : "text-slate-700")}
              >
                {optionLabel(option)}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}

function toDateTimeLocal(value) {
  if (!value || value === "Not Specified" || value === "On Site") return "";
  const text = String(value).trim();
  const localMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\s+(\d{1,2}):(\d{2})/);
  if (localMatch) {
    const [, month, day, rawYear, hour, minute] = localMatch;
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${hour.padStart(2, "0")}:${minute}`;
  }
  const isoMatch = text.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return isoMatch ? `${isoMatch[1]}T${isoMatch[2]}` : "";
}

function toLocalTimeLabel(value) {
  if (!value) return "Not Specified";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return "Not Specified";
  return `${match[2]}/${match[3]}/${match[1].slice(-2)} ${match[4]}:${match[5]} LT`;
}

export function OperationalEditDrawer({ open, onClose, job, saving, onSave, searchAirports, searchFbos }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      airport: String(job.airport?.id || ""),
      fbo: String(job.fbo?.id || ""),
      on_site: Boolean(job.on_site),
      arrival: toDateTimeLocal(job.arrival_formatted_date || job.estimatedETA),
      departure: toDateTimeLocal(job.departure_formatted_date || job.estimatedETD),
      completeBy: toDateTimeLocal(job.complete_before_formatted_date || job.completeBy),
    });
  }, [job, open]);

  const airports = job.airport?.id ? [job.airport] : [];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleAirportChange = (option) => {
    const airport = option ? String(option.id) : "";
    setForm((current) => ({ ...current, airport, fbo: "" }));
  };

  const handleSave = async () => {
    const payload = {
      airport: Number(form.airport),
      fbo: Number(form.fbo),
      on_site: Boolean(form.on_site),
      estimatedETA: form.on_site || !form.arrival ? null : form.arrival,
      estimatedETD: form.departure || null,
      completeBy: form.completeBy || null,
      arrival_formatted_date: form.on_site ? "On Site" : toLocalTimeLabel(form.arrival),
      departure_formatted_date: toLocalTimeLabel(form.departure),
      complete_before_formatted_date: toLocalTimeLabel(form.completeBy),
    };
    if (await onSave(payload)) onClose();
  };

  return (
    <EditDrawer
      open={open}
      onClose={onClose}
      title="Operational summary"
      description="Update the location and operational dates for this job."
      saving={saving}
      onSave={handleSave}
    >
      <div className="space-y-5">
        <SearchSelectField
          label="Airport"
          name="airport"
          value={form.airport || ""}
          onSelect={handleAirportChange}
          onSearch={searchAirports}
          initialOptions={airports}
          enabled={open}
          optionLabel={(airport) => airport?.initials ? `${airport.initials} — ${airport.name}` : airport?.name || ""}
        />
        <SearchSelectField
          key={`fbo-${form.airport || "none"}`}
          label="FBO"
          name="fbo"
          value={form.fbo || ""}
          onSelect={(option) => setForm((current) => ({ ...current, fbo: option ? String(option.id) : "" }))}
          onSearch={(query) => searchFbos(query, form.airport ? Number(form.airport) : null)}
          initialOptions={job.fbo?.id ? [job.fbo] : []}
          enabled={open && Boolean(form.airport)}
          emptyLabel="None"
        />
        <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-3">
          <span className="font-semibold text-slate-700">Aircraft is already on site</span>
          <input
            type="checkbox"
            checked={Boolean(form.on_site)}
            onChange={(event) => setForm((current) => ({ ...current, on_site: event.target.checked }))}
            className="h-4 w-4 rounded border-slate-300 text-[#E3222A] focus:ring-[#E3222A]"
          />
        </label>
        {!form.on_site && <MilitaryDateTimeField label="Arrival" name="arrival" value={form.arrival || ""} onChange={handleChange} />}
        <MilitaryDateTimeField label="Departure" name="departure" value={form.departure || ""} onChange={handleChange} />
        <MilitaryDateTimeField label="Complete by" name="completeBy" value={form.completeBy || ""} onChange={handleChange} />
      </div>
    </EditDrawer>
  );
}

export function JobInformationEditDrawer({
  open,
  onClose,
  job,
  callSign,
  canEditInternalFields,
  canEditCallSign,
  canEditPrice,
  saving,
  onSave,
  searchAircraftTypes,
  searchCustomers,
}) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      tailNumber: (job.tailNumber || "").toUpperCase(),
      aircraftType: String(job.aircraftType?.id || ""),
      customer: String(job.customer?.id || ""),
      price: String(job.price ?? ""),
      status: job.status || "",
      customer_purchase_order: job.customer_purchase_order || "",
      requested_by:
        job.requested_by ||
        `${job.created_by?.first_name || ""} ${job.created_by?.last_name || ""}`.trim(),
      integration_customer: String(job.integration_customer?.id || ""),
      ident: callSign || "",
    });
  }, [callSign, job, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === "tailNumber" ? value.toUpperCase() : value,
    }));
  };

  const handleSave = async () => {
    const payload = {
      tailNumber: form.tailNumber.trim().toUpperCase(),
      aircraftType: Number(form.aircraftType),
      customer_purchase_order: form.customer_purchase_order.trim() || null,
    };
    if (canEditInternalFields) {
      payload.customer = Number(form.customer);
      payload.status = form.status;
      payload.requested_by = form.requested_by.trim() || null;
      payload.integration_customer = form.integration_customer
        ? Number(form.integration_customer)
        : null;
    }
    if (canEditPrice && form.price !== "") payload.price = Number(form.price);
    if (canEditCallSign) payload.ident = form.ident.trim();
    if (await onSave(payload)) onClose();
  };

  const aircraftTypes = job.aircraftType?.id ? [job.aircraftType] : [];
  const customerOptions = uniqueOptions([job.customer, job.integration_customer]);

  return (
    <EditDrawer open={open} onClose={onClose} title="Job information" description="Update the aircraft and identifying job information." saving={saving} onSave={handleSave}>
      <div className="space-y-5">
        <Field label="Tail number" name="tailNumber" value={form.tailNumber || ""} onChange={handleChange} autoCapitalize="characters" />
        <SearchSelectField
          label="Aircraft type"
          name="aircraftType"
          value={form.aircraftType || ""}
          onSelect={(option) => setForm((current) => ({ ...current, aircraftType: option ? String(option.id) : "" }))}
          onSearch={searchAircraftTypes}
          initialOptions={aircraftTypes}
          enabled={open}
        />
        {canEditInternalFields && (
          <>
            <SearchSelectField
              label="Customer"
              name="customer"
              value={form.customer || ""}
              onSelect={(option) => setForm((current) => ({ ...current, customer: option ? String(option.id) : "" }))}
              onSearch={searchCustomers}
              initialOptions={customerOptions}
              enabled={open}
            />
            <SelectField label="Status" name="status" value={form.status || ""} onChange={handleChange} options={JOB_STATUS_OPTIONS} />
          </>
        )}
        {canEditPrice && <Field label="Price" name="price" type="number" min="0" step="0.01" value={form.price ?? ""} onChange={handleChange} />}
        <Field label="Customer P.O." name="customer_purchase_order" value={form.customer_purchase_order || ""} onChange={handleChange} />
        {canEditInternalFields && (
          <>
            <Field label="Requested by" name="requested_by" value={form.requested_by || ""} onChange={handleChange} />
            <SearchSelectField
              label="Integration customer"
              name="integration_customer"
              value={form.integration_customer || ""}
              onSelect={(option) => setForm((current) => ({ ...current, integration_customer: option ? String(option.id) : "" }))}
              onSearch={searchCustomers}
              initialOptions={customerOptions}
              enabled={open}
              emptyLabel="None"
            />
          </>
        )}
        {canEditCallSign && <Field label="Call sign" name="ident" value={form.ident || ""} onChange={handleChange} />}
      </div>
    </EditDrawer>
  );
}

function parseFollowerEmails(value) {
  const uniqueEmails = new Map();
  value
    .split(/[\n,]+/)
    .map((email) => email.trim())
    .filter(Boolean)
    .forEach((email) => {
      const key = email.toLocaleLowerCase();
      if (!uniqueEmails.has(key)) uniqueEmails.set(key, email);
    });
  return [...uniqueEmails.values()];
}

export function FollowersEditDrawer({
  open,
  onClose,
  followers,
  customerId,
  loadCustomerFollowerEmails,
  saving,
  onSave,
}) {
  const [emails, setEmails] = useState("");
  const [error, setError] = useState("");
  const [customerFollowers, setCustomerFollowers] = useState([]);
  const [loadingCustomerFollowers, setLoadingCustomerFollowers] = useState(false);
  const [customerFollowersError, setCustomerFollowersError] = useState("");

  useEffect(() => {
    if (!open) return;
    setEmails((followers || []).map((follower) => follower.email).join("\n"));
    setError("");
  }, [followers, open]);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setCustomerFollowersError("");

    if (!customerId || !loadCustomerFollowerEmails) {
      setCustomerFollowers([]);
      setLoadingCustomerFollowers(false);
      return () => { active = false; };
    }

    setLoadingCustomerFollowers(true);
    loadCustomerFollowerEmails(customerId)
      .then((results) => {
        if (active) setCustomerFollowers(results || []);
      })
      .catch(() => {
        if (active) {
          setCustomerFollowers([]);
          setCustomerFollowersError("Customer follower emails could not be loaded.");
        }
      })
      .finally(() => {
        if (active) setLoadingCustomerFollowers(false);
      });

    return () => { active = false; };
  }, [customerId, loadCustomerFollowerEmails, open]);

  const selectedEmailKeys = useMemo(
    () => new Set(parseFollowerEmails(emails).map((email) => email.toLocaleLowerCase())),
    [emails],
  );

  const toggleCustomerFollower = (email) => {
    const values = parseFollowerEmails(emails);
    const emailKey = email.toLocaleLowerCase();
    const isSelected = values.some((value) => value.toLocaleLowerCase() === emailKey);
    setEmails(
      (isSelected
        ? values.filter((value) => value.toLocaleLowerCase() !== emailKey)
        : [...values, email]
      ).join("\n"),
    );
    setError("");
  };

  const handleSave = async () => {
    const values = parseFollowerEmails(emails);
    const invalidEmail = values.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmail) {
      setError(`${invalidEmail} is not a valid email address.`);
      return;
    }
    if (await onSave(values)) onClose();
  };

  return (
    <EditDrawer open={open} onClose={onClose} title="Followers" description="Followers receive job email notifications." saving={saving} onSave={handleSave}>
      <label className="block" htmlFor="inline-edit-followers">
        <span className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">Job follower emails</span>
        <textarea
          id="inline-edit-followers"
          value={emails}
          onChange={(event) => { setEmails(event.target.value); setError(""); }}
          rows={8}
          placeholder="One email address per line"
          className="mt-2 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-slate-500 focus:ring-slate-200"
        />
      </label>
      {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}
      <p className="mt-3 text-xs text-slate-500">Enter one address per line or separate addresses with commas.</p>
      <div className="pt-3">
        <p className="text-xs font-bold uppercase tracking-[0.06em] text-slate-500">
          Customer follower emails
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Select any email configured on this customer to add it to the job.
        </p>
        <div className="mt-3 space-y-2">
          {loadingCustomerFollowers && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
              Loading customer followers...
            </p>
          )}
          {!loadingCustomerFollowers && customerFollowersError && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
              {customerFollowersError}
            </p>
          )}
          {!loadingCustomerFollowers && !customerFollowersError && customerFollowers.length === 0 && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
              No follower emails are configured for this customer.
            </p>
          )}
          {!loadingCustomerFollowers && customerFollowers.map((follower) => {
            const isSelected = selectedEmailKeys.has(follower.email.toLocaleLowerCase());
            return (
              <button
                key={follower.id || follower.email}
                type="button"
                onClick={() => toggleCustomerFollower(follower.email)}
                aria-label={`${isSelected ? "Remove" : "Add"} follower email ${follower.email}`}
                aria-pressed={isSelected}
                className={classNames(
                  "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition",
                  isSelected
                    ? "border-sky-300 bg-sky-50 text-slate-900"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                )}
              >
                <span className={classNames(
                  "inline-flex h-5 w-5 flex-none items-center justify-center rounded border",
                  isSelected
                    ? "border-sky-600 bg-sky-600 text-white"
                    : "border-slate-300 bg-white text-transparent",
                )}>
                  <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{follower.email}</span>
                <span className="text-xs font-semibold text-slate-500">
                  {isSelected ? "Added" : "Add"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

    </EditDrawer>
  );
}

export function InvoiceEditDrawer({ open, onClose, invoice, saving, onSave, searchVendors }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      vendor: String(invoice?.vendor?.id || ""),
      vendor_charge: String(invoice?.vendor?.charge ?? 0),
      vendor_additional_cost: String(invoice?.vendor?.additional_cost ?? 0),
      internal_additional_cost: String(invoice?.internal_additional_cost ?? 0),
    });
  }, [invoice, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };
  const handleSave = async () => {
    const payload = {
      vendor: form.vendor ? Number(form.vendor) : null,
      vendor_charge: Number(form.vendor_charge || 0),
      vendor_additional_cost: Number(form.vendor_additional_cost || 0),
      internal_additional_cost: Number(form.internal_additional_cost || 0),
    };
    if (await onSave(payload)) onClose();
  };
  const vendors = invoice?.vendor?.id ? [invoice.vendor] : [];

  return (
    <EditDrawer open={open} onClose={onClose} title="Invoice details" description="Update job costs used to calculate profit." saving={saving} onSave={handleSave}>
      <div className="space-y-5">
        <SearchSelectField
          label="Vendor"
          name="vendor"
          value={form.vendor || ""}
          onSelect={(option) => setForm((current) => ({ ...current, vendor: option ? String(option.id) : "" }))}
          onSearch={searchVendors}
          initialOptions={vendors}
          enabled={open}
          emptyLabel="None"
        />
        <Field label="Vendor charge" name="vendor_charge" type="number" min="0" step="0.01" value={form.vendor_charge || ""} onChange={handleChange} />
        <Field label="Vendor additional cost" name="vendor_additional_cost" type="number" min="0" step="0.01" value={form.vendor_additional_cost || ""} onChange={handleChange} />
        <Field label="Internal cost" name="internal_additional_cost" type="number" min="0" step="0.01" value={form.internal_additional_cost || ""} onChange={handleChange} />
      </div>
    </EditDrawer>
  );
}

export function LaborEditDrawer({ open, onClose, job, saving, onSave }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      hours_worked: String(job.hours_worked ?? 0),
      minutes_worked: String(job.minutes_worked ?? 0),
      number_of_workers: String(job.number_of_workers ?? 0),
    });
  }, [job, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };
  const handleSave = async () => {
    const hours = Math.max(0, Number.parseInt(form.hours_worked || "0", 10));
    const minutes = Math.min(59, Math.max(0, Number.parseInt(form.minutes_worked || "0", 10)));
    const workers = Math.max(0, Number.parseInt(form.number_of_workers || "0", 10));
    const payload = {
      hours_worked: hours,
      minutes_worked: minutes,
      number_of_workers: workers,
      labor_time: (hours + minutes / 60) * workers,
    };
    if (await onSave(payload)) onClose();
  };

  return (
    <EditDrawer open={open} onClose={onClose} title="Labor time" description="Update the crew size and time worked." saving={saving} onSave={handleSave}>
      <div className="space-y-5">
        <Field label="Hours worked" name="hours_worked" type="number" min="0" step="1" value={form.hours_worked || ""} onChange={handleChange} />
        <Field label="Minutes worked" name="minutes_worked" type="number" min="0" max="59" step="1" value={form.minutes_worked || ""} onChange={handleChange} />
        <Field label="Number of workers" name="number_of_workers" type="number" min="0" step="1" value={form.number_of_workers || ""} onChange={handleChange} />
      </div>
    </EditDrawer>
  );
}

function tagLabel(tag) {
  return tag.tag_short_name || tag.short_name || tag.tag_name || tag.name;
}

function tagColor(tag) {
  return tag.tag_color || tag.color || "slate";
}

function resolveTagId(tag, availableTags) {
  if (tag.tag_id) return tag.tag_id;
  return availableTags.find((option) => option.name === tag.tag_name)?.id;
}

export function JobTagsEditor({ tags = [], availableTags = [], canEdit, saving, onSave }) {
  const selectedIds = useMemo(
    () => tags.map((tag) => resolveTagId(tag, availableTags)).filter(Boolean),
    [availableTags, tags],
  );
  const remainingTags = availableTags.filter((tag) => !selectedIds.includes(tag.id));

  const removeTag = (tag) => {
    const tagId = resolveTagId(tag, availableTags);
    if (tagId) onSave(selectedIds.filter((id) => id !== tagId));
  };

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {tags.map((tag) => (
        <span
          key={tag.id || tagLabel(tag)}
          className={classNames(
            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
            TAG_STYLES[tagColor(tag)] || TAG_STYLES.slate,
          )}
        >
          {tagLabel(tag)}
          {canEdit && (
            <button
              type="button"
              onClick={() => removeTag(tag)}
              disabled={saving}
              aria-label={`Remove ${tagLabel(tag)} tag`}
              className="rounded-full p-0.5 hover:bg-black/10 disabled:opacity-50"
            >
              <XIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </span>
      ))}
      {canEdit && (
        <Popover className="relative">
          <Popover.Button
            disabled={saving}
            aria-label="Add job tag"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
          </Popover.Button>
          <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="opacity-0 translate-y-1" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Popover.Panel className="absolute left-0 z-50 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
              <p className="px-2 pb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Add tag</p>
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {remainingTags.length ? remainingTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => onSave([...selectedIds, tag.id])}
                    className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left hover:bg-slate-50"
                  >
                    <span className={classNames("rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", TAG_STYLES[tagColor(tag)] || TAG_STYLES.slate)}>
                      {tagLabel(tag)}
                    </span>
                    <PlusIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  </button>
                )) : (
                  <p className="px-2 py-3 text-sm text-slate-500">No more tags available.</p>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </Popover>
      )}
      {!tags.length && !canEdit && <span className="text-xs text-slate-500">No tags</span>}
      {saving && <span className="text-xs text-slate-500">Saving…</span>}
    </div>
  );
}
